/**
 * ChatWidget.jsx — Widget de chat Physiology
 *
 * Estados del widget: CLOSED → FORM (online) | FORM (closed) | BUZON | WAITING | CHAT
 * Horarios y status:
 *   - El status "En línea / Cerrado" se calcula con BUSINESS_HOURS (zona local del cliente).
 *   - Cuando está cerrado, se muestra foto de portada + horario plegable + buzón.
 *   - TODO: cuando el backend exponga GET /api/agents/status público, intersectar con
 *     "hay al menos un agente conectado" para un indicador 100% real.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useCustomer } from 'context/CustomerContext';
import { getMyDistributorStatus, getChatTicket } from 'services/medusa/distributor';
import './ChatWidget.css';

// ─── ASSETS ──────────────────────────────────────────────────────
const ASSET_LOGO         = '/images/customer-service/Imagotipo.svg';
const ASSET_CLOCK        = '/images/customer-service/clock_svg.svg';
const ASSET_CLOSED_COVER = '/images/customer-service/Foto_portada_chat_cerrado.webp';

// ─── HORARIO DE ATENCIÓN ─────────────────────────────────────────
// Mon-Fri 8am-6pm. Sábado y Domingo cerrado.
const SCHEDULE = {
  1: { open: 8, close: 18, label: 'Lunes' },
  2: { open: 8, close: 18, label: 'Martes' },
  3: { open: 8, close: 18, label: 'Miércoles' },
  4: { open: 8, close: 18, label: 'Jueves' },
  5: { open: 8, close: 18, label: 'Viernes' },
};
const ALL_DAYS = [
  { idx: 1, label: 'Lunes' },
  { idx: 2, label: 'Martes' },
  { idx: 3, label: 'Miércoles' },
  { idx: 4, label: 'Jueves' },
  { idx: 5, label: 'Viernes' },
  { idx: 6, label: 'Sábado' },
  { idx: 0, label: 'Domingo' },
];

// Reduce la foto a máx. 1600 px y la pasa a JPEG (data URL) para mandarla
// en el mensaje sin pesar varios MB.
const MAX_PHOTO_SIDE = 1600;
const photoToDataUrl = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; // PNG con transparencia → fondo blanco en JPEG
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen')); };
  img.src = url;
});

const formatHour = (h) => {
  const period = h >= 12 ? 'p.m.' : 'a.m.';
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:00 ${period}`;
};

const isOpenNow = (now = new Date()) => {
  const cfg = SCHEDULE[now.getDay()];
  if (!cfg) return false;
  const t = now.getHours() + now.getMinutes() / 60;
  return t >= cfg.open && t < cfg.close;
};

const nextOpenInfo = (now = new Date()) => {
  const cur = now.getDay();
  const t = now.getHours() + now.getMinutes() / 60;
  const today = SCHEDULE[cur];
  if (today && t < today.open) {
    return { dayLabel: 'hoy', timeLabel: formatHour(today.open) };
  }
  for (let i = 1; i <= 7; i++) {
    const d = (cur + i) % 7;
    if (SCHEDULE[d]) {
      return {
        dayLabel: i === 1 ? 'mañana' : `el ${SCHEDULE[d].label}`,
        timeLabel: formatHour(SCHEDULE[d].open),
      };
    }
  }
  return null;
};

// ─── ICONOS SVG ──────────────────────────────────────────────────
const IconChat = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
);
const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
);
const IconMinimize = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
const IconSound = ({ muted }) =>
  muted ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
  );
const IconMenu = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}><polyline points="6 9 12 15 18 9"/></svg>
);
const IconChevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}><polyline points="6 9 12 15 18 9"/></svg>
);
const IconBack = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
);
const IconAttach = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
);

// ─── ESTADOS DEL CHAT ────────────────────────────────────────────
const STATES = {
  CLOSED:    'closed',
  MINIMIZED: 'minimized',
  FORM:      'form',
  BUZON:     'buzon',
  WAITING:   'waiting',
  CHAT:      'chat',
};

// ─── QUICK REPLIES (placeholders, sin acción hasta cablear) ──────
const QUICK_REPLIES = [
  { id: 'tracking', label: 'Rastrear envío' },
  { id: 'returns',  label: 'Devoluciones' },
  { id: 'product',  label: 'Ayuda sobre un producto' },
  { id: 'virtual',  label: 'Agente virtual' },
];

export default function ChatWidget({
  serverUrl    = 'http://localhost:4000',
  companyName  = 'Physiology',
  welcomeText  = '¡Hola! ¿En qué podemos ayudarte?',
  primaryColor = '#2f3237',
  position     = 'bottom-right',
  accountUrl   = '/account',
  ordersUrl    = '/account/orders',
}) {
  const [state,          setState]          = useState(STATES.CLOSED);
  const { isAuthenticated, token } = useCustomer();
  // null = no sabemos aún; true/false tras consultar el backend
  const [isDistributor,  setIsDistributor]  = useState(null);
  const [distLoading,    setDistLoading]    = useState(false);
  const ticketRef     = useRef(null);  // ticket firmado → bandeja privada de distribución
  const customerIdRef = useRef(null);  // para reanudar la conversación tras reconexión
  const [messages,       setMessages]       = useState([]);
  const [inputValue,     setInputValue]     = useState('');
  const [name,           setName]           = useState('');
  const [email,          setEmail]          = useState('');
  const [formError,      setFormError]      = useState('');
  const [agentTyping,    setAgentTyping]    = useState(false);
  const [agentJoined,    setAgentJoined]    = useState(false);
  const [unreadCount,    setUnreadCount]    = useState(0);
  const [conversationId, setConversationId] = useState(null);
  const [soundEnabled,   setSoundEnabled]   = useState(true);
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [hoursOpen,      setHoursOpen]      = useState(false);
  const [photoFile,      setPhotoFile]      = useState(null);
  const [photoPreview,   setPhotoPreview]   = useState('');

  // Buzón fields (separados del chat para no mezclar estados)
  const [buzonName,    setBuzonName]    = useState('');
  const [buzonEmail,   setBuzonEmail]   = useState('');
  const [buzonMessage, setBuzonMessage] = useState('');
  const [buzonSent,    setBuzonSent]    = useState(false);

  // Re-evaluamos isOpenNow cada minuto para que el estado del badge cambie sin recargar
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  // Override de testing: ?force=open en la URL fuerza el estado abierto.
  // Útil para QA local fuera de horas; en producción no impacta a clientes
  // a menos que se compartan deliberadamente esa URL.
  const forceOpen = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('force') === 'open';
  const open24x7 = forceOpen || isOpenNow();
  const nextOpen = nextOpenInfo();
  // tick is read implicitly via the re-render; reference it to satisfy linters
  void tick;

  const socketRef      = useRef(null);
  const conversationIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const typingTimer    = useRef(null);
  const menuRef        = useRef(null);
  const fileInputRef   = useRef(null);
  const buzonFileRef   = useRef(null);

  const scrollBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  useEffect(() => { scrollBottom(); }, [messages, agentTyping, scrollBottom]);

  // ── Cerrar dropdown al hacer click fuera ──────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Sonido de notificación ────────────────────────────────────
  const playNotification = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch {}
  }, [soundEnabled]);

  // ── Socket: solo cuando el usuario está activamente en chat o en espera ──
  // Antes se conectaba apenas se abría el panel, gastando recursos del servidor
  // sin razón. Ahora solo en WAITING o CHAT, evita logs ruidosos en local.
  const isActive = state === STATES.WAITING || state === STATES.CHAT;
  useEffect(() => {
    if (!isActive) return;
    const socket = io(serverUrl, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    // Al reconectar, el socket nuevo no está en la sala de la conversación.
    socket.io.on('reconnect', () => {
      if (conversationIdRef.current && customerIdRef.current) {
        socket.emit('customer-resume', { conversationId: conversationIdRef.current, customerId: customerIdRef.current });
      }
    });

    socket.on('chat-ready', ({ conversationId: cid, customerId, inbox }) => {
      customerIdRef.current = customerId;
      conversationIdRef.current = cid;
      if (inbox === 'distribution') addSystemMessage('Atención directa a distribuidores: tu mensaje llega directo a dirección.');
      setConversationId(cid);
      setState(STATES.WAITING);
      addSystemMessage('Conectando con un agente…');
    });
    socket.on('agent-joined', ({ agentName }) => {
      setAgentJoined(true);
      setState(STATES.CHAT);
      addSystemMessage(`${agentName} se ha unido al chat.`);
    });
    socket.on('new-message', (msg) => {
      if (msg.senderType === 'agent' || msg.sender_type === 'agent') {
        setMessages((prev) => [...prev, { ...msg, type: 'agent' }]);
        setUnreadCount((n) => n + 1);
      }
    });
    socket.on('agent-typing', () => {
      setAgentTyping(true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setAgentTyping(false), 3000);
    });
    socket.on('conversation-closed', () => {
      addSystemMessage('El agente ha cerrado el chat. ¡Gracias por contactarnos!');
    });

    return () => { socket.disconnect(); };
  }, [isActive, serverUrl]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.type === 'agent') playNotification();
  }, [messages, playNotification]);

  const addSystemMessage = (text) => {
    setMessages((prev) => [...prev, { id: Date.now(), type: 'system', content: text }]);
  };

  // ── Iniciar conversación ──────────────────────────────────────
  const startChat = () => {
    if (!name.trim()) { setFormError('Por favor ingresa tu nombre.'); return; }
    setFormError('');
    ticketRef.current = null;
    setState(STATES.WAITING); // monta el socket; cuando abre, emite customer-start
  };

  // Distribuidora con sesión: pedimos un ticket firmado a Medusa y abrimos el
  // chat en la bandeja privada (solo la ve el fundador).
  const startDistributorChat = async () => {
    setFormError('');
    setDistLoading(true);
    try {
      const t = await getChatTicket(token);
      ticketRef.current = t.ticket;
      if (!name.trim()) setName(t.name || '');
      if (!email.trim()) setEmail(t.email || '');
      setState(STATES.WAITING);
    } catch {
      setFormError('No pudimos verificar tu cuenta de distribución. Intenta de nuevo.');
    } finally {
      setDistLoading(false);
    }
  };

  const openSiteLogin = () => {
    minimize();
    window.dispatchEvent(new Event('phys:open-login'));
  };
  // Cuando el socket conecta y estamos en WAITING sin conversationId, emitimos el start
  useEffect(() => {
    if (state !== STATES.WAITING || conversationId || !socketRef.current) return;
    const s = socketRef.current;
    const onConnect = () => {
      s.emit('customer-start', {
        name: name.trim(),
        email: email.trim() || undefined,
        distributorTicket: ticketRef.current || undefined,
      });
    };
    if (s.connected) onConnect();
    else s.on('connect', onConnect);
    return () => s.off('connect', onConnect);
  }, [state, conversationId, name, email]);

  // ── Mensaje del cliente ───────────────────────────────────────
  const sendMessage = async () => {
    const content = inputValue.trim();
    if (!content && !photoFile) return;
    if (!conversationId) return;
    let image;
    if (photoFile) {
      try {
        image = await photoToDataUrl(photoFile);
      } catch {
        alert('No pudimos leer la imagen. Intenta con otra.');
        return;
      }
    }
    socketRef.current?.emit('customer-message', { conversationId, content, image });
    setMessages((prev) => [...prev, { id: Date.now(), type: 'customer', content, image }]);
    setInputValue('');
    clearPhoto();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (conversationId) socketRef.current?.emit('customer-typing', { conversationId });
  };

  // ── Adjuntar foto (chat activo y buzón) ───────────────────────
  const onPhotoSelected = (e, target = 'chat') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen debe pesar menos de 5 MB.');
      return;
    }
    const url = URL.createObjectURL(file);
    if (target === 'chat') {
      setPhotoFile(file);
      setPhotoPreview(url);
    } else {
      // Buzón: no necesitamos otro slot, reusamos los mismos states
      setPhotoFile(file);
      setPhotoPreview(url);
    }
  };
  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview('');
  };

  // ── Buzón submit ──────────────────────────────────────────────
  const submitBuzon = async (e) => {
    e?.preventDefault?.();
    if (!buzonName.trim() || !buzonMessage.trim()) {
      setFormError('Por favor completa nombre y mensaje.');
      return;
    }
    setFormError('');
    try {
      const photo = photoFile ? await photoToDataUrl(photoFile).catch(() => undefined) : undefined;
      // Distribuidoras: el buzón también va a la bandeja privada.
      let distributorTicket;
      if (isDistributor && token) {
        distributorTicket = await getChatTicket(token).then((t) => t.ticket).catch(() => undefined);
      }
      const res = await fetch(`${serverUrl}/api/buzon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:     buzonName.trim(),
          email:    buzonEmail.trim() || undefined,
          message:  buzonMessage.trim(),
          hasPhoto: !!photoFile,
          photo,
          distributorTicket,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setBuzonSent(true);
    } catch (err) {
      console.error('Error enviando buzón:', err);
      setFormError('No pudimos enviar tu mensaje. Intenta de nuevo en un momento.');
    }
  };
  const resetBuzon = () => {
    setBuzonName('');
    setBuzonEmail('');
    setBuzonMessage('');
    setBuzonSent(false);
    clearPhoto();
    setFormError('');
  };

  // ── ¿La clienta con sesión es distribuidora? (solo al abrir el panel) ──
  const panelOpen = state !== STATES.CLOSED && state !== STATES.MINIMIZED;
  useEffect(() => { setIsDistributor(null); }, [token]);
  useEffect(() => {
    if (!token || !panelOpen || isDistributor !== null) return;
    let cancelled = false;
    getMyDistributorStatus(token)
      .then((s) => { if (!cancelled) setIsDistributor(Boolean(s?.is_distributor)); })
      .catch(() => { if (!cancelled) setIsDistributor(false); });
    return () => { cancelled = true; };
  }, [token, panelOpen, isDistributor]);

  // ── Abrir / minimizar / logout ────────────────────────────────
  const openWidget = () => {
    setUnreadCount(0);
    if (state === STATES.CLOSED)        setState(STATES.FORM);
    else if (state === STATES.MINIMIZED) setState(agentJoined ? STATES.CHAT : (conversationId ? STATES.WAITING : STATES.FORM));
  };
  // Permite abrir el chat desde cualquier parte del sitio:
  // window.dispatchEvent(new Event('phys:open-chat'))
  const openWidgetRef = useRef(openWidget);
  openWidgetRef.current = openWidget;
  useEffect(() => {
    const handler = () => openWidgetRef.current();
    window.addEventListener('phys:open-chat', handler);
    return () => window.removeEventListener('phys:open-chat', handler);
  }, []);
  const minimize = () => {
    if (state !== STATES.CLOSED && state !== STATES.MINIMIZED) {
      setMenuOpen(false);
      setState(STATES.MINIMIZED);
    }
  };
  const logout = () => {
    setMenuOpen(false);
    socketRef.current?.disconnect();
    socketRef.current = null;
    setState(STATES.CLOSED);
    setMessages([]);
    setConversationId(null);
    conversationIdRef.current = null;
    customerIdRef.current = null;
    ticketRef.current = null;
    setAgentJoined(false);
    setName('');
    setEmail('');
    resetBuzon();
    setUnreadCount(0);
  };

  const panelVisible = state !== STATES.CLOSED && state !== STATES.MINIMIZED;
  const positionClass = position === 'bottom-left' ? 'cw-left' : 'cw-right';

  // ──────────────────────────────────────────────────────────────
  // Sub-render: Hours panel (plegable)
  const renderHoursIndicator = () => {
    const isOpen = open24x7;
    return (
      <div className={`cw-hours ${hoursOpen ? 'is-expanded' : ''}`}>
        <button className="cw-hours-trigger" onClick={() => setHoursOpen((v) => !v)} type="button">
          <img src={ASSET_CLOCK} alt="" className="cw-hours-icon" />
          <span className={`cw-hours-status ${isOpen ? 'is-online' : 'is-closed'}`}>
            {isOpen ? 'Abierto' : 'Cerrado'}
          </span>
          <IconChevron open={hoursOpen} />
          {!hoursOpen && !isOpen && nextOpen && (
            <span className="cw-hours-next">
              Abre {nextOpen.dayLabel} a las {nextOpen.timeLabel}
            </span>
          )}
        </button>

        {hoursOpen && (
          <ul className="cw-hours-list">
            {ALL_DAYS.map((d) => {
              const cfg = SCHEDULE[d.idx];
              return (
                <li key={d.idx}>
                  <span>{d.label}</span>
                  <span>{cfg ? `${formatHour(cfg.open)} – ${formatHour(cfg.close)}` : 'Cerrado'}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  // Click en chip → envía el texto como mensaje del cliente para que el agente
  // responda manualmente. Cuando se conecte la automatización por sección
  // (tracking, devoluciones, etc.) se reemplaza este handler por el específico.
  const sendChipMessage = (text) => {
    if (!conversationId || !agentJoined) return;
    socketRef.current?.emit('customer-message', { conversationId, content: text });
    setMessages((prev) => [...prev, { id: Date.now(), type: 'customer', content: text }]);
  };

  // Sub-render: chips en CHAT activo, antes de que el cliente haya tipeado nada
  const renderChips = () => (
    <div className="cw-chips">
      {QUICK_REPLIES.map((q) => (
        <button
          key={q.id}
          className="cw-chip"
          type="button"
          onClick={() => sendChipMessage(q.label)}
        >
          {q.label}
        </button>
      ))}
    </div>
  );

  // Sub-render: estado abierto (formulario para iniciar chat)
  const renderFormOpen = () => (
    <div className="cw-form">
      <p className="cw-welcome">{welcomeText}</p>
      <div className="cw-field">
        <label>Tu nombre *</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="¿Cómo te llamamos?" onKeyDown={(e) => e.key === 'Enter' && startChat()} />
      </div>
      <div className="cw-field">
        <label>Correo electrónico (opcional)</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="para enviarte un resumen" />
      </div>
      {formError && <p className="cw-error">{formError}</p>}
      <button className="cw-cta-btn" onClick={startChat}>Iniciar chat</button>
      {renderDistributorLine()}
    </div>
  );

  // Línea "Atención directa a distribuidores" debajo del CTA.
  function renderDistributorLine(onImage = false) {
    const cls = `cw-dist-line${onImage ? ' cw-dist-line--on-image' : ''}`;
    if (!isAuthenticated) {
      return (
        <p className={cls}>
          Atención directa a distribuidores.{' '}
          <button type="button" className="cw-dist-link" onClick={openSiteLogin}>Iniciar sesión.</button>
        </p>
      );
    }
    if (!isDistributor) return null;
    if (onImage) {
      return <p className={cls}>Distribuidora: tu mensaje en el buzón llega directo a dirección.</p>;
    }
    return (
      <button type="button" className="cw-cta-btn cw-dist-btn" onClick={startDistributorChat} disabled={distLoading}>
        {distLoading ? 'Verificando…' : 'Atención directa a distribuidores'}
      </button>
    );
  }

  // Sub-render: estado cerrado — foto llena el body, horario y buzón flotan encima
  const renderFormClosed = () => (
    <div className="cw-closed">
      <img className="cw-closed-bg" src={ASSET_CLOSED_COVER} alt="" />
      <div className="cw-closed-overlay">
        {renderHoursIndicator()}
        <button className="cw-cta-btn cw-cta-on-image" onClick={() => setState(STATES.BUZON)}>
          Dejar un mensaje en el buzón
        </button>
        {renderDistributorLine(true)}
      </div>
    </div>
  );

  // Sub-render: buzón
  const renderBuzon = () => {
    if (buzonSent) {
      return (
        <div className="cw-buzon cw-buzon-success">
          <button className="cw-back-btn" type="button" onClick={() => { resetBuzon(); setState(STATES.FORM); }}>
            <IconBack /> Volver
          </button>
          <h3>¡Gracias!</h3>
          <p>Hemos recibido tu mensaje. Te contactaremos en cuanto un agente esté disponible.</p>
        </div>
      );
    }
    return (
      <form className="cw-buzon" onSubmit={submitBuzon}>
        <button className="cw-back-btn" type="button" onClick={() => setState(STATES.FORM)}>
          <IconBack /> Volver
        </button>
        <p className="cw-welcome">Cuéntanos en qué podemos ayudarte. Te contestamos en horario laboral.</p>
        <div className="cw-field">
          <label>Tu nombre *</label>
          <input value={buzonName} onChange={(e) => setBuzonName(e.target.value)} placeholder="¿Cómo te llamamos?" />
        </div>
        <div className="cw-field">
          <label>Correo electrónico</label>
          <input type="email" value={buzonEmail} onChange={(e) => setBuzonEmail(e.target.value)} placeholder="para responderte" />
        </div>
        <div className="cw-field">
          <label>Mensaje *</label>
          <textarea rows={4} value={buzonMessage} onChange={(e) => setBuzonMessage(e.target.value)} placeholder="Escribe tu consulta…" />
        </div>

        <div className="cw-attach-row">
          <input ref={buzonFileRef} type="file" accept="image/*" onChange={(e) => onPhotoSelected(e, 'buzon')} hidden />
          <button type="button" className="cw-attach-btn" onClick={() => buzonFileRef.current?.click()}>
            <IconAttach />
            Adjuntar foto (opcional)
          </button>
          {photoPreview && (
            <div className="cw-photo-thumb">
              <img src={photoPreview} alt="adjunto" />
              <button type="button" className="cw-photo-remove" onClick={clearPhoto} aria-label="Quitar foto">×</button>
            </div>
          )}
        </div>

        {formError && <p className="cw-error">{formError}</p>}
        <button type="submit" className="cw-cta-btn">Enviar mensaje</button>
      </form>
    );
  };

  // Sub-render: chat activo (mensajes + input)
  const customerHasTyped = messages.some((m) => m.type === 'customer');
  const showChips = state === STATES.CHAT && agentJoined && !customerHasTyped;

  const renderChat = () => (
    <>
      <div className="cw-messages">
        {messages.map((msg, i) => (
          <div key={msg.id || i} className={`cw-msg cw-msg-${msg.type}`}>
            {msg.type === 'system'
              ? <div className="cw-msg-system">{msg.content}</div>
              : (
                <div className="cw-bubble">
                  {(msg.image || msg.attachmentId) && (
                    <img
                      className="cw-bubble-photo"
                      src={msg.image || `${serverUrl}/api/attachments/${msg.attachmentId}`}
                      alt="Foto adjunta"
                    />
                  )}
                  {msg.content}
                </div>
              )}
          </div>
        ))}
        {agentTyping && (
          <div className="cw-msg cw-msg-agent">
            <div className="cw-bubble cw-typing"><span/><span/><span/></div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {showChips && renderChips()}

      {photoPreview && (
        <div className="cw-input-photo">
          <img src={photoPreview} alt="adjunto" />
          <button type="button" className="cw-photo-remove" onClick={clearPhoto} aria-label="Quitar foto">×</button>
        </div>
      )}

      <div className="cw-input-area">
        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => onPhotoSelected(e, 'chat')} hidden />
        <button type="button" className="cw-attach-btn-icon" onClick={() => fileInputRef.current?.click()} disabled={!agentJoined} aria-label="Adjuntar foto">
          <IconAttach />
        </button>
        <textarea
          ref={inputRef}
          className="cw-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Escribe tu mensaje…"
          disabled={!agentJoined}
          rows={1}
        />
        <button className="cw-send-btn" onClick={sendMessage} disabled={(!inputValue.trim() && !photoFile) || !agentJoined}>
          <IconSend />
        </button>
      </div>
    </>
  );

  return (
    <div className={`cw-container ${positionClass}`} style={{ '--cw-primary': primaryColor }}>
      {panelVisible && (
        <div className="cw-panel">
          {/* Header */}
          <div className="cw-header">
            <div className="cw-header-info">
              <div className="cw-avatar">
                <img src={ASSET_LOGO} alt={companyName} />
              </div>
              <div>
                <div className="cw-company-name">{companyName}</div>
                <div className="cw-status">
                  {state === STATES.WAITING && <><span className="cw-dot pulsing"/>Buscando agente…</>}
                  {state === STATES.CHAT    && <><span className="cw-dot online"/>En línea</>}
                  {(state === STATES.FORM || state === STATES.BUZON) && (
                    open24x7
                      ? <><span className="cw-dot online"/>En línea</>
                      : <><span className="cw-dot"/>Soporte cerrado</>
                  )}
                </div>
              </div>
            </div>

            <div className="cw-header-actions">
              <button className="cw-icon-btn" onClick={() => setSoundEnabled((s) => !s)} aria-label="Sonido" title={soundEnabled ? 'Silenciar' : 'Activar sonido'}>
                <IconSound muted={!soundEnabled}/>
              </button>

              <div className="cw-menu-wrapper" ref={menuRef}>
                <button className="cw-icon-btn" onClick={() => setMenuOpen((o) => !o)} aria-label="Menú">
                  <IconMenu open={menuOpen}/>
                </button>
                {menuOpen && (
                  <div className="cw-dropdown">
                    <a href={accountUrl} className="cw-dropdown-item" onClick={() => setMenuOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      Ir a mi cuenta
                    </a>
                    <a href={ordersUrl} className="cw-dropdown-item" onClick={() => setMenuOpen(false)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                      Ir a mis pedidos
                    </a>
                    <div className="cw-dropdown-divider"/>
                    <button className="cw-dropdown-item cw-dropdown-danger" onClick={logout}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      Cerrar sesión del chat
                    </button>
                  </div>
                )}
              </div>

              <button className="cw-icon-btn" onClick={minimize} aria-label="Minimizar">
                <IconMinimize/>
              </button>
            </div>
          </div>

          {state === STATES.FORM && (open24x7 ? renderFormOpen() : renderFormClosed())}
          {state === STATES.BUZON && renderBuzon()}
          {(state === STATES.WAITING || state === STATES.CHAT) && renderChat()}
        </div>
      )}

      {/* FAB */}
      <button className="cw-fab" onClick={openWidget} aria-label="Abrir chat">
        <IconChat/>
        {unreadCount > 0 && <span className="cw-badge">{unreadCount}</span>}
      </button>
    </div>
  );
}
